import { useState } from 'react';
import { getKnowledgeBase, deleteKnowledgeBase, seedKnowledgeBase, GetKnowledgeBaseOutputType } from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Trash2, Database, Search, RefreshCw, Loader2, BookOpen } from 'lucide-react';

type Entry = GetKnowledgeBaseOutputType['entries'][0];

const TYPE_COLORS: Record<string, string> = {
  'SOP': 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  'Training Module': 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  'Handbook': 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  'Policy': 'bg-red-500/10 text-red-400 border-red-500/20',
  'Reference': 'bg-violet-500/10 text-violet-400 border-violet-500/20',
};

interface Props {
  entries: Entry[];
  loading: boolean;
  onRefresh: () => void;
}

export default function KnowledgeBaseManager({ entries, loading, onRefresh }: Props) {
  const [search, setSearch] = useState('');
  const [seeding, setSeeding] = useState(false);

  const filtered = entries.filter(e =>
    e.title.toLowerCase().includes(search.toLowerCase()) ||
    e.sourceType.toLowerCase().includes(search.toLowerCase()) ||
    e.tags.some(t => t.toLowerCase().includes(search.toLowerCase()))
  );

  const removeEntry = async (id: string) => {
    try {
      await deleteKnowledgeBase({ id });
      toast.success('Entry removed from knowledge bank');
      onRefresh();
    } catch { toast.error('Failed to remove entry'); }
  };

  const seed = async () => {
    setSeeding(true);
    try {
      const res = await seedKnowledgeBase({ force: false });
      if (res.entriesCreated > 0) {
        toast.success(`🧠 Seeded ${res.entriesCreated} entries from Rattlebone SOPs!`);
        onRefresh();
      } else {
        toast.info(res.message);
      }
    } catch { toast.error('Seeding failed'); }
    finally { setSeeding(false); }
  };

  const forceSeed = async () => {
    setSeeding(true);
    try {
      const res = await seedKnowledgeBase({ force: true });
      toast.success(res.message);
      onRefresh();
    } catch { toast.error('Seeding failed'); }
    finally { setSeeding(false); }
  };

  if (loading) return <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14" />)}</div>;

  return (
    <div className="space-y-4">
      {/* Header actions */}
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="flex items-center gap-2 flex-1 max-w-sm">
          <Search className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search knowledge bank..." className="h-8 text-sm" />
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={onRefresh}>
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
          {entries.length === 0 ? (
            <Button size="sm" onClick={seed} disabled={seeding} className="bg-primary text-primary-foreground">
              {seeding ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Seeding...</> : <><Database className="w-3.5 h-3.5 mr-1.5" /> Seed from Rattlebone PDFs</>}
            </Button>
          ) : (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="outline" disabled={seeding}>
                  <Database className="w-3.5 h-3.5 mr-1.5" /> Re-seed
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Re-seed Knowledge Bank?</AlertDialogTitle>
                </AlertDialogHeader>
                <p className="text-sm text-muted-foreground px-6">This will add all Rattlebone SOP entries again. Existing entries won't be removed — it only adds missing ones.</p>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={forceSeed}>Re-seed</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        {['SOP', 'Training Module', 'Handbook', 'Policy'].map(type => (
          <div key={type} className="border border-border p-3 text-center">
            <p className="text-lg font-bold text-foreground">{entries.filter(e => e.sourceType === type).length}</p>
            <p className="text-xs text-muted-foreground">{type}s</p>
          </div>
        ))}
      </div>

      {entries.length === 0 && (
        <div className="border border-dashed border-border p-12 text-center">
          <BookOpen className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
          <p className="font-medium text-foreground mb-1">Knowledge Bank is empty</p>
          <p className="text-sm text-muted-foreground mb-4">Seed it with all Rattlebone SOP and training content from the uploaded PDFs, or add content via the Upload tab.</p>
          <Button onClick={seed} disabled={seeding} className="bg-primary text-primary-foreground">
            {seeding ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Seeding...</> : <><Database className="w-4 h-4 mr-2" /> Seed from Rattlebone PDFs ({26} documents)</>}
          </Button>
        </div>
      )}

      {filtered.length > 0 && (
        <div className="border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground">Document</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground hidden sm:table-cell">Type</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground hidden lg:table-cell">Tags</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-muted-foreground hidden xl:table-cell">Preview</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map(e => (
                <tr key={e.id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3">
                    <p className="font-medium text-foreground text-sm">{e.title}</p>
                    {e.sourceModuleId && <p className="text-xs text-muted-foreground mt-0.5">Linked to module</p>}
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    <span className={`text-xs px-2 py-0.5 border ${TYPE_COLORS[e.sourceType] || 'bg-muted text-muted-foreground border-border'}`}>
                      {e.sourceType}
                    </span>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <div className="flex flex-wrap gap-1">
                      {e.tags.slice(0, 2).map(t => (
                        <span key={t} className="text-xs px-1.5 py-0.5 bg-muted text-muted-foreground border border-border">{t}</span>
                      ))}
                      {e.tags.length > 2 && <span className="text-xs text-muted-foreground">+{e.tags.length - 2}</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden xl:table-cell text-xs text-muted-foreground max-w-xs truncate">
                    {e.contentPreview}
                  </td>
                  <td className="px-4 py-3">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive hover:text-destructive"><Trash2 className="w-3 h-3" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Remove "{e.title}" from knowledge bank?</AlertDialogTitle></AlertDialogHeader>
                        <p className="text-sm text-muted-foreground px-6">The AI assistant will no longer have access to this content.</p>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => removeEntry(e.id)}>Remove</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {filtered.length === 0 && search && (
        <p className="text-center text-sm text-muted-foreground py-8">No entries match "{search}"</p>
      )}

      <p className="text-xs text-muted-foreground text-center">
        {entries.length} entries · The AI Training Assistant uses all entries as context when answering staff questions
      </p>
    </div>
  );
}
